import AttainmentTable from "../../../DynamicComponents/CO-PO Attainment/AttainmentTable";
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

// FinalCoPoView.jsx - Compact version with expand/collapse
const FinalCOPOView = ({
  course,
  academicYear,
  semesterType,
  darkMode,
  attainmentData,
  isLoading,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    finalAttainment: true,
    sectionWise: false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (isLoading) {
    return (
      <div
        className={`p-3 rounded-lg mb-6 ${
          darkMode
            ? "bg-[#161B22] border border-[#22232B]"
            : "bg-gray-50 border border-[#D1D5DB]"
        }`}
      >
        <div className="flex justify-center items-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          <span
            className={`ml-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Loading attainment data...
          </span>
        </div>
      </div>
    );
  }

  if (!attainmentData) {
    return (
      <div
        className={`p-3 rounded-lg mb-6 ${
          darkMode
            ? "bg-[#161B22] border border-[#22232B]"
            : "bg-gray-50 border border-[#D1D5DB]"
        }`}
      >
        <h4
          className={`text-base font-medium mb-2 ${
            darkMode ? "text-white" : "text-gray-700"
          }`}
        >
          Final CO-PO Attainment for {course.course_code}
        </h4>
        <p
          className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          No attainment data available for academic year {academicYear},{" "}
          {semesterType} semester.
        </p>
      </div>
    );
  }

  const { courseAverages, sections } = attainmentData;

  // Format the course averages data to match the AttainmentTable component's expected format
  const formatAveragesData = (averages, type) => {
    return averages.map((item) => ({
      [`${type}_id`]: item[`${type}_number`],
      [`${type}_number`]: item[`${type}_number`],
      direct_attainment: item[`avg_direct_attainment`],
      indirect_attainment: item[`avg_indirect_attainment`],
      final_attainment: item[`avg_final_attainment`],
    }));
  };

  const courseAveragesData = {
    coAttainment: formatAveragesData(courseAverages.coAttainment, "co"),
    poAttainment: formatAveragesData(courseAverages.poAttainment, "po"),
    psoAttainment:
      courseAverages.psoAttainment && courseAverages.psoAttainment.length > 0
        ? formatAveragesData(courseAverages.psoAttainment, "pso")
        : null,
  };

  return (
    <div className="space-y-4 mb-6">
      {/* Course Averages Section - Collapsible */}
      <div
        className={`rounded-lg ${darkMode ? "bg-[#1A1F2E] border border-[#2D3748]" : "bg-white border border-gray-200"}`}
      >
        <button
          className="flex items-center justify-between w-full p-3 text-left"
          onClick={() => toggleSection("finalAttainment")}
        >
          <div className="flex items-center">
            <div
              className={`w-1 h-6 rounded-full mr-2 ${darkMode ? "bg-blue-500" : "bg-blue-600"}`}
            ></div>
            <h3
              className={`font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}
            >
              Final Attainments (All Sections Combined)
            </h3>
          </div>
          {expandedSections.finalAttainment ? (
            <ChevronDown
              size={18}
              className={darkMode ? "text-gray-400" : "text-gray-500"}
            />
          ) : (
            <ChevronRight
              size={18}
              className={darkMode ? "text-gray-400" : "text-gray-500"}
            />
          )}
        </button>

        {expandedSections.finalAttainment && (
          <div className="px-3 pb-3">
            <div className="p-2 rounded mb-2">
              <AttainmentTable
                data={courseAveragesData}
                darkMode={darkMode}
                isMainTable={true}
              />
            </div>
            <p
              className={`text-xs ${darkMode ? "text-blue-300" : "text-blue-600"}`}
            >
              Weighted average attainment across all sections
            </p>
          </div>
        )}
      </div>

      {/* Section-wise Attainment - Collapsible */}
      <div
        className={`rounded-lg ${darkMode ? "bg-[#161B22] border border-[#22232B]" : "bg-gray-50 border border-gray-200"}`}
      >
        <button
          className="flex items-center justify-between w-full p-3 text-left"
          onClick={() => toggleSection("sectionWise")}
        >
          <div className="flex items-center">
            <div
              className={`w-1 h-6 rounded-full mr-2 ${darkMode ? "bg-green-500" : "bg-green-600"}`}
            ></div>
            <h3
              className={`font-semibold ${darkMode ? "text-white" : "text-gray-800"}`}
            >
              Section-wise Attainment ({sections.length} sections)
            </h3>
          </div>
          {expandedSections.sectionWise ? (
            <ChevronDown
              size={18}
              className={darkMode ? "text-gray-400" : "text-gray-500"}
            />
          ) : (
            <ChevronRight
              size={18}
              className={darkMode ? "text-gray-400" : "text-gray-500"}
            />
          )}
        </button>

        {expandedSections.sectionWise && (
          <div className="px-3 pb-3">
            {sections.length === 0 ? (
              <div
                className={`p-3 rounded text-center text-sm ${darkMode ? "bg-[#161B22] text-gray-400" : "bg-gray-100 text-gray-500"}`}
              >
                No section data available
              </div>
            ) : (
              <div className="space-y-3">
                {sections.map((section, index) => (
                  <div
                    key={section.section_id}
                    className={`p-2 rounded ${darkMode ? "bg-[#1A1F2E]" : "bg-white"} ${index % 2 === 0 ? (darkMode ? "bg-opacity-70" : "bg-opacity-90") : ""}`}
                  >
                    <h4
                      className={`text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Section {section.section_name}
                    </h4>
                    <AttainmentTable
                      data={{
                        coAttainment: section.coAttainment,
                        poAttainment: section.poAttainment,
                        psoAttainment: section.psoAttainment,
                      }}
                      darkMode={darkMode}
                      isMainTable={false}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalCOPOView;
